import * as React from 'react'
import EditIcon from '@mui/icons-material/Edit'
import PreviewIcon from '@mui/icons-material/Preview'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import {
  GridRowsProp,
  DataGrid,
  GridColDef,
  GridActionsCellItem,
  GridRowId,
  GridToolbar,
} from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { ProfileModel } from 'types'
import axios from 'axios'
import { base_url } from 'env'
import { toast } from 'react-hot-toast'
import { Box } from '@mui/material'
import SearchBar from './SearchBar'
import ExportButton from './ExportButton'

const searchFields = [
  { value: 'firstName', label: 'Prénom' },
  { value: 'lastName', label: 'Nom' },
  { value: 'grade', label: 'Classe' },
  { value: 'createdBy', label: 'Créé par (Login)' },
  { value: 'id', label: 'ID du Profil' },
  { value: 'timeTarget', label: 'Objectif (min)' },
]

export default function ProfileDataGrid({
  profiles,
}: {
  profiles: ProfileModel[]
}) {
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [filteredProfiles, setFilteredProfiles] = React.useState(profiles)
  const navigate = useNavigate()

  React.useEffect(() => {
    const newRows = filteredProfiles.map((value) => ({
      ...value, // Spread all properties from ProfileModel
      id: value.id,
      birthday: dayjs.unix(Number(value.birthday)).toDate(),
      creationDate: dayjs.unix(Number(value.creationDate)).toDate(),
    }))
    setRows(newRows)
  }, [filteredProfiles])

  const handlePreviewClick = (id: GridRowId) => () => {
    navigate('/profile/' + id)
  }

  const handleEditClick = (id: GridRowId) => () => {
    navigate('/profile/' + id + '/edit')
  }

  const handleDeleteClick = (id: GridRowId) => () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ce profil ?`)) {
      axios
        .delete(`${base_url}/profile/${id}`)
        .then(() => {
          toast.success('Profil supprimé avec succès.')
          setRows((prevRows) => prevRows.filter((row) => row.id !== id))
        })
        .catch((err) => {
          console.error('Failed to delete profile:', err)
        })
    }
  }

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Identifiant',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'firstName',
      headerName: 'Prénom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'lastName',
      headerName: 'Nom',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'grade',
      headerName: 'Classe',
      width: 100,
    },
    {
      field: 'timeTarget',
      headerName: 'Objectif (min)',
      type: 'number',
      width: 120,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      cellClassName: 'actions',
      getActions: ({ id }) => [
        <GridActionsCellItem
          icon={<PreviewIcon />}
          label="Aperçu"
          onClick={handlePreviewClick(id)}
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Modifier"
          onClick={handleEditClick(id)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Supprimer"
          onClick={handleDeleteClick(id)}
        />,
      ],
    },
  ]

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          margin: '1rem',
          gap: '1rem',
        }}
      >
        <SearchBar
          onSearch={setFilteredProfiles}
          fields={searchFields}
          originalData={profiles}
        />
        <ExportButton
          fields={[
            'id',
            'firstName',
            'lastName',
            'grade',
            'birthday',
            'active',
            'creationDate',
            'testLocked',
            'createdBy',
            'surveyId',
            'suggestedActivity',
            'title',
            'timeTarget',
          ]}
          data={profiles}
          exportFileName="profiles_export"
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        slots={{ toolbar: GridToolbar }}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        autosizeOnMount={true}
      />
    </Box>
  )
}
